import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import {
  setAutomationsPills,
  setAlertsPills,
  setAssignmentPills,
  setDocumentationPills,
  setDedupePills,
  setMatchingPills,
  setScheduledAssignmentPills,
} from './canvasPillsReducer';
import calculateSweepElementsPills from './calculateSweepElementsPills';
import { calculateDocumentationPills } from './calculateDocumentationPills';
import { getDeployedVersions } from '../documentation/selected-object/filters/utils';
import { selectCrmOrgs } from '../pages/environments/environmentsReducer';
import { RootState } from '../../reducers';

export const useCalculatePills = ({ funnelMap }: { funnelMap: FunnelMap }) => {
  const dispatch = useDispatch();
  const crmOrgs = useSelector(selectCrmOrgs);

  // Get global environments state to access data for all orgs
  const globalEnvironments = useSelector((state: RootState) => state.global.environments);

  useEffect(() => {
    crmOrgs.forEach((crmOrg) => {
      const crmOrgId = crmOrg.id;
      const orgData = globalEnvironments[crmOrgId]?.data;

      const automations = orgData?.automations;
      const alerts = orgData?.alerts;
      const assignments = orgData?.assignments;
      const scheduledAssignments = orgData?.scheduledAssignments;
      const dedup = orgData?.dedup;
      const matching = orgData?.matching;

      const _automations = getDeployedVersions(automations ?? []);
      const _alerts = getDeployedVersions(alerts ?? []);
      const _assignments = getDeployedVersions(assignments ?? []);
      const _scheduledAssignments = getDeployedVersions(scheduledAssignments ?? []);
      const _dedup = getDeployedVersions(dedup ?? []);
      const _matching = getDeployedVersions(matching ?? []);

      if (_automations) {
        dispatch(
          setAutomationsPills({ crmOrgId, pills: calculateSweepElementsPills(_automations) }),
        );
      }
      if (_alerts) {
        dispatch(setAlertsPills({ crmOrgId, pills: calculateSweepElementsPills(_alerts) }));
      }
      if (_assignments) {
        dispatch(
          setAssignmentPills({ crmOrgId, pills: calculateSweepElementsPills(_assignments) }),
        );
      }
      if (_scheduledAssignments) {
        dispatch(
          setScheduledAssignmentPills({
            crmOrgId,
            pills: calculateSweepElementsPills(_scheduledAssignments),
          }),
        );
      }
      if (_dedup) {
        dispatch(setDedupePills({ crmOrgId, pills: calculateSweepElementsPills(_dedup) }));
      }
      if (_matching) {
        dispatch(setMatchingPills({ crmOrgId, pills: calculateSweepElementsPills(_matching) }));
      }
    });
  }, [crmOrgs, globalEnvironments, dispatch]);

  // Calculate documentation pills for all CRM orgs
  useEffect(() => {
    if (!funnelMap?.funnelsData) return;

    crmOrgs.forEach((crmOrg) => {
      const crmOrgId = crmOrg.id;
      const orgData = globalEnvironments[crmOrgId]?.data;
      const parsedRules = orgData?.documentation?.onlyActiveParsedRules;
      const parsedRecordTypes = orgData?.documentation?.parsedRecordTypes;

      if (parsedRecordTypes && parsedRules) {
        dispatch(
          setDocumentationPills({
            crmOrgId,
            documentation: calculateDocumentationPills({
              funnelsData: funnelMap.funnelsData,
              recordTypesData: funnelMap.recordTypesData || {},
              recordTypes: funnelMap.recordTypes || {},
              parsedRecordTypes,
              parsedRules,
            }),
          }),
        );
      }
    });
  }, [
    dispatch,
    funnelMap?.funnelsData,
    funnelMap?.recordTypesData,
    funnelMap?.recordTypes,
    globalEnvironments,
    crmOrgs,
  ]);
};
